import { router, usePage } from "@inertiajs/react"
import { useCallback, useMemo } from "react"
import { toast } from "sonner"
import {
  EntityScaleSheetModal,
  type EntityScaleSheetHeightUpdate,
  type EntityScaleSheetLoadResult,
  type EntityScaleSheetRow,
} from "@/components/scale-sheet/entity-scale-sheet-modal"
import { useTranslation } from "@/hooks/use-translation"
import { propFrontViewBusy } from "@/lib/prop-jobs"
import {
  DEFAULT_PROP_HEIGHT_CM,
  fetchPropScaleSheet,
  patchPropHeights,
  PROP_GRIP_DRAG_MIN_HEIGHT_CM,
  PROP_MIN_HEIGHT_CM,
} from "@/lib/prop-scale-sheet-api"
import { generatePropFrontView } from "@/lib/props-api"
import { parseScaleSheetEntityKey } from "@/lib/scale-sheet-layout"
import type { WorkspaceBaseProps } from "@/types/workspace"

type Props = {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  propIds?: number[]
  shotId?: number
  variant?: "project" | "shot"
  closeOnApply?: boolean
  onApplied?: () => void
  showExportActions?: boolean
}

function toEntityRows(
  props: {
    id: number
    name: string
    saved_height_cm: number | null
    effective_height_cm: number
    front_view_url: string | null
    image_width?: number
    image_height?: number
  }[],
): EntityScaleSheetRow[] {
  return props.map((p) => ({
    id: p.id,
    name: p.name,
    saved_height_cm: p.saved_height_cm,
    effective_height_cm: p.effective_height_cm,
    scale_pose_url: p.front_view_url,
    image_width: p.image_width,
    image_height: p.image_height,
    entity_type: "prop" as const,
  }))
}

async function loadPropScaleSheet(
  projectId: number,
  entityIds: number[] | undefined,
  options: { preview: boolean; shotId?: number; editOnly: boolean },
): Promise<EntityScaleSheetLoadResult> {
  const res = await fetchPropScaleSheet(projectId, entityIds, options)
  return {
    status: res.status,
    idsKey: res.prop_ids_key,
    image_url: res.image_url,
    image_download_url: res.image_download_url,
    layout_meta: res.layout_meta,
    rows: toEntityRows(res.props),
  }
}

async function patchPropScaleHeights(projectId: number, updates: EntityScaleSheetHeightUpdate[]) {
  await patchPropHeights(
    projectId,
    updates.map((u) => ({ prop_id: u.id, height_cm: u.height_cm })),
  )
}

export function PropScaleSheetModal({
  projectId,
  open,
  onOpenChange,
  propIds,
  shotId,
  variant = "project",
  closeOnApply = variant === "project",
  onApplied,
  showExportActions,
}: Props) {
  const { t } = useTranslation(["workspace"])
  const { jobs = [] } = usePage<WorkspaceBaseProps>().props

  const labels = useMemo(
    () => ({
      close: t("propsPage.close"),
      title: t("propsPage.scaleSheetTitle"),
      previewTitle: t("propsPage.scaleSheetPreviewTitle"),
      hint: t("propsPage.scaleSheetHint"),
      needTwo: t("propsPage.scaleSheetNeedTwo"),
      applyAll: t("propsPage.scaleSheetApplyAll"),
      heightLabel: t("propsPage.scaleSheetHeightLabel"),
    }),
    [t],
  )

  const regenerateFigureTitle = t("propsPage.regenerateFrontView")

  const isFigureBusy = useCallback(
    (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed?.entity_type !== "prop") return false
      return propFrontViewBusy(jobs, parsed.id)
    },
    [jobs],
  )

  const handleRegenerateFigure = useCallback(
    async (entityKey: string) => {
      const parsed = parseScaleSheetEntityKey(entityKey)
      if (parsed?.entity_type !== "prop") return
      try {
        await generatePropFrontView(projectId, parsed.id)
        router.reload({ only: ["jobs"] })
      } catch (e) {
        toast.error(e instanceof Error ? e.message : String(e))
        throw e
      }
    },
    [projectId],
  )

  return (
    <EntityScaleSheetModal
      projectId={projectId}
      open={open}
      onOpenChange={onOpenChange}
      entityIds={propIds}
      shotId={shotId}
      variant={variant}
      closeOnApply={closeOnApply}
      onApplied={onApplied}
      showExportActions={showExportActions}
      loadSheet={loadPropScaleSheet}
      patchHeights={patchPropScaleHeights}
      labels={labels}
      waitingStatuses={["waiting_figure"]}
      exportFilenamePrefix="prop-scale-sheet"
      defaultHeightCm={DEFAULT_PROP_HEIGHT_CM}
      missingFigurePlaceholder="prop"
      minHeightCm={PROP_MIN_HEIGHT_CM}
      minDragHeightCm={PROP_GRIP_DRAG_MIN_HEIGHT_CM}
      minEntities={1}
      figureEntityType="prop"
      onRegenerateFigure={handleRegenerateFigure}
      regenerateFigureTitle={regenerateFigureTitle}
      isFigureBusy={isFigureBusy}
    />
  )
}
